import type * as Geo from "@/base/geo"
import type * as Models from "@/redux/models"
import type { Reducer } from "@reduxjs/toolkit"

import * as AppStorage from "@/base/async-storage"
import { deleteEventActionTriggersAsync } from "@/base/farmhq-api"
import { loadActiveFarmAsync } from "@/base/load-app"
import { logger } from "@/base/logger"
import {
  createAction,
  createSelector,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit"

import type { RootState } from "./root.reducer"

import { clickCancel, setActiveFeature } from "./actions"
import { useRootDispatch } from "./useRootDispatch"
import { useRootSelector } from "./useRootSelector"

export type StatusMapFeatureName =
  | "changeFarmAccount"
  | "createField"
  | "itemList"
  | "quickActions"
  | "seedDatabase"
export const MAP_LIST_TYPES = ["devices", "fields", "timeline"] as const

export type SelectedItem =
  | {
      id: number
      kind:
        | "deviceConnection"
        | "field"
        | "reelRun"
        | "centerPivot"
        | "linearIrrigator"
    }
  | {
      id: string
      kind: "device" | "deviceActivity"
    }

export interface SelectReelRunPayload extends Models.ReelRunIdProps {
  centerLatLng: Geo.MultiPoint | undefined
}

export type SelectReelRunHandler = (runData: SelectReelRunPayload) => void

export interface MapVisibilityState {
  showFieldLabels?: boolean
  showRunCompletionText?: boolean
  /** Pivot slot markers (and their labels) only render when the user has
   * zoomed in enough to make individual stops legible. Native sets this
   * via a region.latitudeDelta threshold; web sets it via the GoogleMap
   * `zoom_changed` listener. */
  showPivotSlots?: boolean
}

export type SelectedDeviceView =
  | "deviceControlPanel"
  | "deviceLocationMenu"
  | "selectedDeviceOptions"
  | "commenting"
  | "pivotControl"
  | "planBuilder"
  | "pivotBuilder"
  | "linearBuilder"

export interface StatusMapState extends MapVisibilityState {
  activeFeature?: StatusMapFeatureName | "seedDatabase"
  deviceStatusFilterKey: AppStorage.StoredValues["deviceStatusFilterKey"]
  /** Plan ID being edited in plan builder (undefined = creating new plan) */
  editingPlanId?: number
  /** True when the device list bottom sheet is at its expanded (85%) snap point */
  isDeviceListSheetExpanded: boolean
  listType: (typeof MAP_LIST_TYPES)[number]
  /** When true, map shows notification status overlays on device markers */
  notificationMode?: boolean
  /** End gun table ID to auto-open in pivot builder (set from config screen) */
  pendingEndGunTableId?: number
  selectedDeviceView?: SelectedDeviceView,
  selectedItem?: SelectedItem
  /** Controls whether irrigation heatmap layers are rendered on the map */
  showHeatmapLayers: boolean
  /** Controls whether device activity visualizations are rendered on the map */
  showDeviceActivity: boolean
}

type SetVisibilityPayload = Partial<MapVisibilityState>

export const setVisibility =
  createAction<SetVisibilityPayload>("SET_VISIBILITY")

export const changeMapListView = createAction<{
  value: StatusMapState["listType"]
}>("CHANGE_LIST_VIEW")

export const clearState = createAction("CLEAR_STATE")

export const selectDevice = createAction<Models.DeviceIdProps>("SELECT_DEVICE")

export const selectField = createAction<Models.FieldIdProps>("SELECT_FIELD")

export const selectReelRun =
  createAction<Models.ReelRunIdProps>("SELECT_REEL_RUN")

export const selectPivot = createAction<{ pivotId: number }>("SELECT_PIVOT")

export const selectDeviceActivity = createAction<Models.DeviceIdProps>(
  "SELECT_DEVICE_ACTIVITY",
)

// TODO: Too many actions here

export const selectDeviceConnection = createAction<{
  id: number
}>("SELECT_CONNECTION")

export const selectCenterPivot = createAction<{
  centerPivotId: number
}>("SELECT_CENTER_PIVOT")

export const clearSelectedCenterPivot = createAction<number | undefined>(
  "CLEAR_SELECTED_CENTER_PIVOT",
)

export const selectLinearIrrigator = createAction<{
  linearIrrigatorId: number
}>("SELECT_LINEAR_IRRIGATOR")

export const clearSelectedLinearIrrigator = createAction<number | undefined>(
  "CLEAR_SELECTED_LINEAR_IRRIGATOR",
)

export const setSelectedDeviceView = createAction<SelectedDeviceView | undefined>(
  "SET_SELECTED_DEVICE_VIEW",
)

export const setEditingPlanId = createAction<number | undefined>(
  "SET_EDITING_PLAN_ID",
)

export const toggleNotificationMode = createAction("statusMap/TOGGLE_NOTIFICATION_MODE")

export const toggleHeatmapLayers = createAction("statusMap/TOGGLE_HEATMAP_LAYERS")

export const toggleDeviceActivityLayers = createAction("statusMap/TOGGLE_DEVICE_ACTIVITY_LAYERS")

export const setDeviceListSheetExpanded = createAction<boolean>(
  "statusMap/SET_DEVICE_LIST_SHEET_EXPANDED",
)

export const setPendingEndGunTableId = createAction<number | undefined>(
  "statusMap/SET_PENDING_END_GUN_TABLE_ID",
)

export const deselectItem = createAction<SelectedItem | undefined>(
  "DESELECT_ITEM",
)
export const clearSelectedDevice = createAction<string | undefined>(
  "CLEAR_SELECTED_DEVICE",
)
export const clearSelectedField = createAction<number | undefined>(
  "CLEAR_SELECTED_FIELD",
)
export const clearSelectedReelRun = createAction<number | undefined>(
  "CLEAR_SELECTED_RUN",
)

const initialState: StatusMapState = {
  deviceStatusFilterKey: "all",
  isDeviceListSheetExpanded: false,
  listType: "devices",
  showDeviceActivity: true,
  showFieldLabels: false,
  // Heatmap fetching and mounting are gated on this flag (ALL-4846), so
  // the default decides whether farms pay the raster load cost every
  // session. Re-enabled by default (ALL-4848) after the S3 signer fix and
  // rendering isolation shipped. Not persisted — resets each session.
  showHeatmapLayers: true,
  showPivotSlots: false,
  showRunCompletionText: false,
}

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(
        AppStorage.readLocalStorageAsync.fulfilled,
        (state, { payload }) => {
          state.deviceStatusFilterKey = payload.deviceStatusFilterKey
        },
      )
      .addCase(
        AppStorage.setDeviceStatusFilterKey.pending,
        (state, action) => {
          state.deviceStatusFilterKey = action.meta.arg
        },
      )
      .addCase(clearSelectedDevice, (state, { payload }) => {
        if (state.selectedItem && state.selectedItem.kind === "device") {
          const isMatch = state.selectedItem.id === payload
          if (typeof payload === "undefined" || isMatch) {
            state.selectedItem = undefined
          }
        }
      })
      .addCase(clearSelectedField, (state, { payload }) => {
        if (state.selectedItem && state.selectedItem.kind === "field") {
          const isMatch = state.selectedItem.id === payload
          if (typeof payload === "undefined" || isMatch) {
            state.selectedItem = undefined
          }
        }
      })
      .addCase(clearSelectedReelRun, (state, { payload }) => {
        if (state.selectedItem && state.selectedItem.kind === "reelRun") {
          const isMatch = state.selectedItem.id === payload
          if (typeof payload === "undefined" || isMatch) {
            state.selectedItem = undefined
          }
        }
      })
      .addCase(selectDevice, (state, { payload }) => {
        state.selectedItem = { id: payload.deviceId, kind: "device" }
        state.activeFeature = undefined
      })
      .addCase(selectField, (state, { payload }) => {
        state.selectedItem = { id: payload.fieldId, kind: "field" }
      })
      .addCase(selectReelRun, (state, { payload }) => {
        state.selectedItem = { id: payload.reelRunId, kind: "reelRun" }
      })
      .addCase(selectDeviceConnection, (state, { payload }) => {
        state.selectedItem = { id: payload.id, kind: "deviceConnection" }
      })
      .addCase(selectDeviceActivity, (state, { payload }) => {
        state.selectedItem = { id: payload.deviceId, kind: "deviceActivity" }
      })
      .addCase(selectPivot, (state, { payload }) => {
        state.selectedItem = { id: payload.pivotId, kind: "centerPivot" }
        state.activeFeature = undefined
        state.selectedDeviceView = undefined
      })
      .addCase(selectCenterPivot, (state, { payload }) => {
        state.selectedItem = { id: payload.centerPivotId, kind: "centerPivot" }
        state.activeFeature = undefined
        state.selectedDeviceView = undefined
      })
      .addCase(clearSelectedCenterPivot, (state, { payload }) => {
        if (state.selectedItem && state.selectedItem.kind === "centerPivot") {
          const isMatch = state.selectedItem.id === payload
          if (typeof payload === "undefined" || isMatch) {
            state.selectedItem = undefined
            state.selectedDeviceView = undefined
          }
        }
      })
      .addCase(selectLinearIrrigator, (state, { payload }) => {
        state.selectedItem = {
          id: payload.linearIrrigatorId,
          kind: "linearIrrigator",
        }
        state.activeFeature = undefined
        state.selectedDeviceView = undefined
      })
      .addCase(clearSelectedLinearIrrigator, (state, { payload }) => {
        if (
          state.selectedItem
          && state.selectedItem.kind === "linearIrrigator"
        ) {
          const isMatch = state.selectedItem.id === payload
          if (typeof payload === "undefined" || isMatch) {
            state.selectedItem = undefined
            state.selectedDeviceView = undefined
          }
        }
      })
      .addCase(deleteEventActionTriggersAsync.fulfilled, (state) => {
        if (state.selectedItem?.kind === "deviceConnection") {
          state.selectedItem = undefined
        }
      })
      .addCase(setVisibility, (state, { payload }) => {
        state.showFieldLabels =
          payload.showFieldLabels ?? state.showFieldLabels
        state.showRunCompletionText =
          payload.showRunCompletionText ?? state.showRunCompletionText
        state.showPivotSlots =
          payload.showPivotSlots ?? state.showPivotSlots
      })
      .addCase(deselectItem, (state) => {
        state.selectedItem = undefined
        state.selectedDeviceView = undefined
      })
      .addCase(setPendingEndGunTableId, (state, { payload }) => {
        state.pendingEndGunTableId = payload
      })
      .addCase(toggleNotificationMode, (state) => {
        state.notificationMode = !state.notificationMode
      })
      .addCase(toggleHeatmapLayers, (state) => {
        state.showHeatmapLayers = !state.showHeatmapLayers
      })
      .addCase(toggleDeviceActivityLayers, (state) => {
        state.showDeviceActivity = !state.showDeviceActivity
      })
      .addCase(setDeviceListSheetExpanded, (state, { payload }) => {
        state.isDeviceListSheetExpanded = payload
      })
      .addCase(setSelectedDeviceView, (state, { payload }) => {
        state.selectedDeviceView = payload
        // Clear editing plan ID when exiting plan builder
        if (payload !== "planBuilder") {
          state.editingPlanId = undefined
        }
        // Clear pending end gun table ID when exiting pivot builder
        if (payload !== "pivotBuilder") {
          state.pendingEndGunTableId = undefined
        }
      })
      .addCase(setEditingPlanId, (state, { payload }) => {
        state.editingPlanId = payload
      })
      .addCase(changeMapListView, (state, { payload }) => {
        state.listType = payload.value
        state.activeFeature = "itemList"
      })
      .addCase(setActiveFeature, (state, { payload }) => {
        if (typeof payload === "string") {
          state.activeFeature = payload
        } else if (payload) {
          state.activeFeature = payload.value
        } else {
          state.activeFeature = payload
        }
        state.selectedItem = undefined
      })
      .addMatcher(
        isAnyOf(
          selectDevice,
          selectField,
          selectReelRun,
          selectPivot,
          selectCenterPivot,
          selectLinearIrrigator,
        ),
        (state) => {
          state.activeFeature = undefined
        },
      )
      .addMatcher(
        isAnyOf(clearState, clickCancel),
        (state) => {
          state.activeFeature = undefined
          state.selectedItem = undefined
          state.selectedDeviceView = undefined
        },
      )
      .addMatcher(
        isAnyOf(AppStorage.setActiveFarmId.fulfilled),
        (state) => {
          // Clear selections when switching farms
          state.selectedItem = undefined
          state.selectedDeviceView = undefined
        },
      )
      .addMatcher(
        isAnyOf(loadActiveFarmAsync.pending),
        (state) => {
          // Only clear activeFeature during farm refresh
          // Keep selections so dialogs stay open
          state.activeFeature = undefined
        },
      ),
  initialState,
  name: "statusMap",
  reducers: {},
})

const statusMap: Reducer<StatusMapState> = slice.reducer
export default statusMap

function getSelectedItem(state: RootState) {
  return state.statusMap.selectedItem
}

export const getSelectedRunIdFromState = createSelector(
  getSelectedItem,
  (selectedItem) => {
    if (selectedItem?.kind === "reelRun") {
      return selectedItem.id
    }
    return undefined
  },
)
export const getSelectedFieldIdFromState = createSelector(
  getSelectedItem,
  (selectedItem) => {
    if (selectedItem?.kind === "field") {
      return selectedItem.id
    }
    return undefined
  },
)
export const getSelectedDeviceIdFromState = createSelector(
  getSelectedItem,
  (selectedItem) => {
    if (selectedItem?.kind === "device") {
      return selectedItem.id
    }
    return undefined
  },
)

export const getSelectedDeviceConnectionIdFromState = createSelector(
  getSelectedItem,
  (selectedItem) => {
    if (selectedItem?.kind === "deviceConnection") {
      return selectedItem.id
    }
    return undefined
  },
)

export const getSelectedDeviceActivityIdFromState = createSelector(
  getSelectedItem,
  (selectedItem) => {
    if (selectedItem?.kind === "deviceActivity") {
      return selectedItem.id
    }
    return undefined
  },
)

export const getSelectedPivotIdFromState = createSelector(
  getSelectedItem,
  (selectedItem) => {
    if (selectedItem?.kind === "centerPivot") {
      return selectedItem.id
    }
    return undefined
  },
)

export const getSelectedCenterPivotIdFromState = createSelector(
  getSelectedItem,
  (selectedItem) => {
    if (selectedItem?.kind === "centerPivot") {
      return selectedItem.id
    }
    return undefined
  },
)

export const getSelectedLinearIrrigatorIdFromState = createSelector(
  getSelectedItem,
  (selectedItem) => {
    if (selectedItem?.kind === "linearIrrigator") {
      return selectedItem.id
    }
    return undefined
  },
)
export function getShowRunProgressTextFromState(state: RootState) {
  return state.statusMap.showRunCompletionText === true
}
export function getAreFieldLabelsVisibleFromState(state: RootState) {
  return state.statusMap.showFieldLabels === true
}
export function getArePivotSlotsVisibleFromState(state: RootState) {
  return state.statusMap.showPivotSlots === true
}
export function getActiveFeatureNameFromState(state: RootState) {
  return state.statusMap.activeFeature
}
export function getStatusMapListViewFromState(state: RootState) {
  return state.statusMap.listType
}
export const getShowAllDevicesFromState = (state: RootState) =>
  state.statusMap.deviceStatusFilterKey === "all"

export function getSelectedDeviceViewFromState(state: RootState) {
  return state.statusMap.selectedDeviceView
}

export function getEditingPlanIdFromState(state: RootState) {
  return state.statusMap.editingPlanId
}

export function getPendingEndGunTableIdFromState(state: RootState) {
  return state.statusMap.pendingEndGunTableId
}

export function getNotificationModeFromState(state: RootState) {
  return state.statusMap.notificationMode === true
}

export function getShowHeatmapLayersFromState(state: RootState) {
  return state.statusMap.showHeatmapLayers
}

export function getShowDeviceActivityFromState(state: RootState) {
  return state.statusMap.showDeviceActivity
}

export function getIsDeviceListSheetExpandedFromState(state: RootState) {
  return state.statusMap.isDeviceListSheetExpanded
}

export function useDeviceStatusFilterKey() {
  const dispatch = useRootDispatch()
  const showAllDevices = useRootSelector(getShowAllDevicesFromState)
  return {
    onToggleShowAllDevices: () => {
      dispatch(
        AppStorage.setDeviceStatusFilterKey(
          showAllDevices ? "visible" : "all",
        ),
      ).catch((error) => {
        logger.error(error)
      })
    },
    showAllDevices,
  }
}
